import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from '../components/layout/Header';
import {
  Trophy, Settings, Users, Video, Crown, ArrowLeft, RefreshCw, Zap, Lightbulb, Bell, Shield, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  QuickActionsTab, 
  WinnersTab, 
  SubmissionsTab, 
  UsersTab, 
  ChallengesTab, 
  SettingsTab,
  NotificationBell,
  FeedbackTab,
  OBSCardsTab,
  ModeratorsTab,
  PayoutsTab
} from '../components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PULL_THRESHOLD = 80;

// OBS Studio Logo SVG
const OBSIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 2.16c5.44 0 9.84 4.4 9.84 9.84 0 5.44-4.4 9.84-9.84 9.84-5.44 0-9.84-4.4-9.84-9.84 0-5.44 4.4-9.84 9.84-9.84zM8.56 6.06c-2.19 0-3.97 1.78-3.97 3.97s1.78 3.97 3.97 3.97c.56 0 1.09-.12 1.57-.33-.19-.45-.3-.94-.3-1.46 0-2.06 1.67-3.73 3.73-3.73.52 0 1.01.11 1.46.3.21-.48.33-1.01.33-1.57 0-2.19-1.78-3.97-3.97-3.97-.56 0-1.09.12-1.57.33.19.45.3.94.3 1.46 0 .21-.02.42-.05.62-.2-.03-.4-.05-.62-.05-.52 0-1.01.11-1.46.3-.21-.48-.33-1.01-.33-1.57 0-.22.02-.43.05-.63-.38.07-.74.18-1.09.33zm6.88 4.15c-2.06 0-3.73 1.67-3.73 3.73s1.67 3.73 3.73 3.73 3.73-1.67 3.73-3.73-1.67-3.73-3.73-3.73z"/>
  </svg>
);

const Admin = () => {
  const navigate = useNavigate();
  const { token, isAdmin, user, loading: authLoading } = useAuth();
  
  // Data state
  const [users, setUsers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [winners, setWinners] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [stats, setStats] = useState({});
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [settings, setSettings] = useState({ 
    prize_amount: 100, 
    prize_currency: 'USD', 
    contest_active: true,
    challenge_title: '',
    challenge_rules: ''
  });
  
  // UI state
  const [dataLoading, setDataLoading] = useState(true);
  const [pickingWinner, setPickingWinner] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Refs
  const touchStartY = useRef(0);
  const contentRef = useRef(null);

  // Refresh data function
  const refreshData = useCallback(async () => {
    if (!token || !isAdmin) {
      toast.error('Not authorized');
      return;
    }
    
    setIsRefreshing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, submissionsRes, settingsRes, winnersRes, challengesRes, statsRes, notifCountRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/submissions`, { headers }),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/winners`, { headers }),
        axios.get(`${API}/admin/challenges`, { headers }),
        axios.get(`${API}/stats`),
        axios.get(`${API}/admin/notifications/count`, { headers }).catch(() => ({ data: { unread_count: 0 } }))
      ]);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setSubmissions(Array.isArray(submissionsRes.data) ? submissionsRes.data : []);
      setSettings(settingsRes.data && typeof settingsRes.data === 'object' ? settingsRes.data : {});
      setWinners(Array.isArray(winnersRes.data) ? winnersRes.data : []);
      setChallenges(Array.isArray(challengesRes.data) ? challengesRes.data : []);
      setStats(statsRes.data && typeof statsRes.data === 'object' ? statsRes.data : {});
      setUnreadNotifications(notifCountRes.data?.unread_count || 0);
      toast.success('Data refreshed');
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error(`Failed to refresh: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [token, isAdmin]);

  // Touch handlers for pull-to-refresh
  const handleTouchStart = useCallback((e) => {
    if (contentRef.current?.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (isRefreshing || contentRef.current?.scrollTop > 0) return;
    
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0 && touchStartY.current > 0) {
      setPullDistance(Math.min(diff * 0.4, PULL_THRESHOLD * 1.5));
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_THRESHOLD) {
      refreshData();
    } else {
      setPullDistance(0);
    }
    touchStartY.current = 0;
  }, [pullDistance, refreshData]);

  // Initial data fetch
  useEffect(() => {
    if (authLoading) return;

    if (!token || !isAdmin) {
      setDataLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [usersRes, submissionsRes, settingsRes, winnersRes, challengesRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/submissions`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/settings`).catch(() => ({ data: {} })),
          axios.get(`${API}/admin/winners`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/challenges`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/stats`).catch(() => ({ data: {} }))
        ]);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setSubmissions(Array.isArray(submissionsRes.data) ? submissionsRes.data : []);
        setSettings(settingsRes.data && typeof settingsRes.data === 'object' ? settingsRes.data : {});
        setWinners(Array.isArray(winnersRes.data) ? winnersRes.data : []);
        setChallenges(Array.isArray(challengesRes.data) ? challengesRes.data : []);
        setStats(statsRes.data && typeof statsRes.data === 'object' ? statsRes.data : {});
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, [token, isAdmin, authLoading]);

  // Pick winner handler - supports custom prize configs
  const handlePickWinner = async (submissionId, place = 1, prizeConfig = null) => {
    setPickingWinner(submissionId);
    try {
      const payload = {
        place,
        challenge_id: submissions.find(s => s.id === submissionId)?.challenge_id || null
      };
      
      // Include prize config if provided
      if (prizeConfig) {
        payload.prize_config = prizeConfig;
      }
      
      await axios.post(`${API}/admin/winners/pick/${submissionId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Winner selected! (${place}${place === 1 ? 'st' : place === 2 ? 'nd' : 'rd'} place)`);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pick winner');
    } finally {
      setPickingWinner(null);
    }
  };

  // End all active challenges
  const handleEndAllChallenges = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const activeChallengeIds = challenges.filter(c => c.status === 'active').map(c => c.id);
      await Promise.all(
        activeChallengeIds.map(id =>
          axios.post(`${API}/admin/challenges/${id}/end`, {}, { headers })
        )
      );
      toast.success(`Ended ${activeChallengeIds.length} challenge(s)`);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end challenges');
    }
  };

  // Clear all submissions
  const handleClearSubmissions = async () => {
    try {
      await axios.post(`${API}/admin/clear-submissions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All submissions cleared');
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear submissions');
    }
  };

  // Delete all ended challenges
  const handleDeleteEndedChallenges = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endedIds = challenges.filter(c => c.status === 'ended').map(c => c.id);
      await Promise.all(
        endedIds.map(id =>
          axios.delete(`${API}/admin/challenges/${id}`, { headers })
        )
      );
      toast.success(`Deleted ${endedIds.length} ended challenge(s)`);
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ended challenges');
    }
  };

  // Auth loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Not authorized
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Admin Access Required</h1>
          <p className="text-gray-400 mb-4">You don&apos;t have permission to access this page.</p>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Home
          </Button>
        </div>
      </div>
    );
  }

  // Data loading state
  if (dataLoading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="flex items-center justify-center pt-32">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-400">Loading admin data...</p>
          </div>
        </div>
      </div>
    );
  }

  const safeUsers = Array.isArray(users) ? users : [];
  const safeSubmissions = Array.isArray(submissions) ? submissions : [];
  const safeWinners = Array.isArray(winners) ? winners : [];
  const safeChallenges = Array.isArray(challenges) ? challenges : [];

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <div 
        ref={contentRef}
        className="pt-20 pb-12 px-4 max-w-7xl mx-auto overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull to refresh indicator */}
        {pullDistance > 0 && (
          <div 
            className="flex items-center justify-center transition-all overflow-hidden"
            style={{ height: pullDistance }}
          >
            <RefreshCw className={`w-6 h-6 text-red-500 ${pullDistance >= PULL_THRESHOLD ? 'animate-spin' : ''}`} />
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              Admin Panel
            </h1>
            <p className="text-gray-500 text-sm">Welcome back, {user?.display_name}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Notification Bell with Count Badge */}
            <div className="relative">
              <NotificationBell token={token} onCountChange={setUnreadNotifications} />
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={refreshData}
              disabled={isRefreshing}
              className="border-red-900/50 hover:bg-red-950/50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="overflow-x-auto pb-2">
            <TabsList className="bg-zinc-900 border border-zinc-800 flex-nowrap min-w-max">
              <TabsTrigger value="dashboard" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <Trophy className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Dashboard</span>
              </TabsTrigger>
              <TabsTrigger value="challenges" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <Zap className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Challenges</span>
              </TabsTrigger>
              <TabsTrigger value="submissions" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <Video className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Submissions</span>
              </TabsTrigger>
              <TabsTrigger value="winners" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <Crown className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Winners</span>
              </TabsTrigger>
              <TabsTrigger value="payouts" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <CreditCard className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Payouts</span>
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <Users className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Users</span>
              </TabsTrigger>
              <TabsTrigger value="feedback" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <Lightbulb className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Feedback</span>
              </TabsTrigger>
              <TabsTrigger value="moderators" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <Shield className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Mods</span>
              </TabsTrigger>
              <TabsTrigger value="obs" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <OBSIcon className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">OBS</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-red-600 text-xs sm:text-sm">
                <Settings className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Settings</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <QuickActionsTab
              stats={stats}
              challenges={safeChallenges}
              submissions={safeSubmissions}
              winners={safeWinners}
              users={safeUsers}
              token={token}
              handlePickWinner={handlePickWinner}
              handleEndAllChallenges={handleEndAllChallenges}
              handleClearSubmissions={handleClearSubmissions}
              handleDeleteEndedChallenges={handleDeleteEndedChallenges}
              onRefresh={refreshData}
              onSwitchTab={setActiveTab}
            />
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <SubmissionsTab
              submissions={safeSubmissions}
              challenges={safeChallenges}
              winners={safeWinners}
              token={token}
              onPickWinner={handlePickWinner}
              pickingWinner={pickingWinner}
              onRefresh={refreshData}
            />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UsersTab
              users={safeUsers}
              winners={safeWinners}
              token={token}
              onRefresh={refreshData}
            />
          </TabsContent>

          {/* Challenges Tab */}
          <TabsContent value="challenges">
            <ChallengesTab
              challenges={safeChallenges}
              token={token}
              onRefresh={refreshData}
            />
          </TabsContent>

          {/* Winners Tab */}
          <TabsContent value="winners">
            <WinnersTab
              winners={safeWinners}
              token={token}
              onRefresh={refreshData}
            />
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <PayoutsTab token={token} onRefresh={refreshData} />
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            <FeedbackTab token={token} />
          </TabsContent>

          {/* Moderators Tab */}
          <TabsContent value="moderators">
            <ModeratorsTab 
              users={safeUsers}
              token={token}
              onRefresh={refreshData}
            />
          </TabsContent>

          {/* OBS Cards Tab */}
          <TabsContent value="obs">
            <OBSCardsTab token={token} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <SettingsTab
              settings={settings}
              token={token}
              onRefresh={refreshData}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
